import { useCallback, useMemo, useState } from 'react'
import { notification } from 'antd'
import isNil from 'lodash/isNil'
import { useBulkGrantAccessMutation } from '../../api/user-invitation-api-client'
import { extractError } from '../../api/extract-error'
import {
  BulkGrantAction,
  BulkGrantResultItem,
  UserListRow,
} from '../../api/dto/user-invitation.dto'
import {
  buildGrantBuckets,
  EMPTY_GRANT_BUCKETS,
  grantableRows,
} from './grantBuckets'
import { failedEmails, mergeRetriedResults } from './grantReport'

interface UseGrantAccessParams {
  selectedRows: UserListRow[]
  /**
   * Called after a bulk run closes, with the emails still needing attention.
   * A second attempt from the bulk bar is then already scoped to them.
   */
  onBulkFinished: (emails: string[]) => void
}

/**
 * The grant flow: which rows the modal is acting on, the run itself, and the
 * per-recipient report. Granting always acts on the table selection.
 */
export const useGrantAccess = ({
  selectedRows,
  onBulkFinished,
}: UseGrantAccessParams) => {
  const [rows, setRows] = useState<UserListRow[] | null>(null)
  const [results, setResults] = useState<BulkGrantResultItem[] | null>(null)
  const [progress, setProgress] = useState<{
    processed: number
    total: number
  } | null>(null)

  // Outcomes are held in state rather than flattened into a toast: a batch
  // routinely finishes partly done, and the reason a recipient was skipped or
  // failed is the whole point of running it.
  const mutation = useBulkGrantAccessMutation({
    onProgress: (processed, total) => setProgress({ processed, total }),
    onSuccess: ({ results: outcomes }) => {
      setProgress(null)
      setResults((curr) => mergeRetriedResults(curr, outcomes))
    },
    onError: (error) => {
      setProgress(null)
      notification.error({
        message: extractError(error, 'Failed to grant access'),
      })
    },
  })

  /** The rows the open modal is acting on, split into what it can and cannot do. */
  const buckets = useMemo(
    () => (isNil(rows) ? EMPTY_GRANT_BUCKETS : buildGrantBuckets(rows)),
    [rows]
  )

  // Recipients skipped by the client-side preview are never sent, so the server
  // cannot report them. Fold them back in: the report has to account for every
  // recipient the confirm step listed, not only the ones that were submitted.
  const report = useMemo(() => {
    if (isNil(results)) return null
    const skipped: BulkGrantResultItem[] = buckets.skip.map(
      ({ row, reason }) => ({
        email: row.email,
        action: BulkGrantAction.Skipped,
        success: false,
        reason,
      })
    )
    return [...results, ...skipped]
  }, [results, buckets])

  const handleOpenBulk = useCallback(() => {
    if (selectedRows.length === 0) return
    setResults(null)
    setProgress(null)
    setRows(selectedRows)
  }, [selectedRows])

  const handleClose = useCallback(() => {
    if (!isNil(results)) {
      onBulkFinished(failedEmails(results))
    }
    setRows(null)
    setResults(null)
    setProgress(null)
  }, [results, onBulkFinished])

  const handleSubmit = useCallback(
    (roleIds: string[]) => {
      const actionable = grantableRows(buckets)
      if (actionable.length === 0) return
      mutation.mutate({
        grants: actionable.map((row) => ({
          email: row.email,
          personId: row.personId,
          clientPublicKeys: row.billingClientPublicKeys,
          roleIds,
        })),
      })
    },
    [buckets, mutation]
  )

  const handleRetryFailed = useCallback(
    (emails: string[], roleIds: string[]) => {
      const wanted = new Set(emails)
      const retried = (rows ?? []).filter((row) => wanted.has(row.email))
      if (retried.length === 0) return
      mutation.mutate({
        grants: retried.map((row) => ({
          email: row.email,
          personId: row.personId,
          clientPublicKeys: row.billingClientPublicKeys,
          roleIds,
        })),
      })
    },
    [rows, mutation]
  )

  return {
    buckets,
    modalOpen: !isNil(rows),
    results: report,
    progress,
    mutation,
    handleOpenBulk,
    handleClose,
    handleSubmit,
    handleRetryFailed,
  }
}
